import React from "react";
import { RoomStats, RoomEarnings } from "../../../entities/room.entity";
import { FiUsers, FiGift, FiDollarSign, FiMic } from "react-icons/fi";

type StatsSectionProps = {
  stats: RoomStats;
  earnings?: RoomEarnings;
};

export const StatsSection: React.FC<StatsSectionProps> = ({ stats, earnings }) => {
  const StatCard = ({
    icon,
    label,
    value,
    subValue,
    color,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    subValue?: string;
    color: string;
  }) => (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${color} text-white`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-white bg-opacity-20 rounded-lg">{icon}</div>
        <span className="text-sm font-medium text-white text-opacity-90">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subValue && <p className="text-xs text-white text-opacity-80 mt-1">{subValue}</p>}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        icon={<FiUsers className="w-5 h-5" />}
        label="Joined Users"
        value={stats.joinedUsers}
        color="from-blue-500 to-blue-600"
      />
      <StatCard
        icon={<FiMic className="w-5 h-5" />}
        label="Joined Stage"
        value={stats.joinedStage || 0}
        color="from-green-500 to-green-600"
      />
      <StatCard
        icon={<FiGift className="w-5 h-5" />}
        label="Gifts Received"
        value={(stats.giftIncomeAmount || 0).toLocaleString()}
        subValue="Total income"
        color="from-purple-500 to-purple-600"
      />
      <StatCard
        icon={<FiGift className="w-5 h-5" />}
        label="Gifts Sent"
        value={(stats.giftSentAmount || 0).toLocaleString()}
        subValue="Total sent"
        color="from-pink-500 to-pink-600"
      />

      {earnings && (
        <>
          <div className="md:col-span-2 lg:col-span-4 border-t border-gray-200 pt-4 mt-2">
            <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
              Earnings ({earnings.currency})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Coach Amount</p>
                <p className="text-lg font-bold text-gray-900">
                  {(earnings.coachAmount || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Business Fee</p>
                <p className="text-lg font-bold text-gray-900">
                  {(earnings.businessFee || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">VAT Amount</p>
                <p className="text-lg font-bold text-gray-900">
                  {(earnings.vatAmount || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <p className="text-xs text-green-600 mb-1">Total Amount</p>
                <p className="text-lg font-bold text-green-700">
                  {(earnings.totalAmount || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
